/**
 * The Risk Review trip table — the plain chart the founder asked for
 * (2026-10-02): Departs · Customer · Ref · Paid · Base · Total · Base now ·
 * Net profit · Last payment. Pure helpers so the page and its tests read
 * every cell the same way. `booked_base`, `last_payment_*`, `payment_issue`
 * and `net_profit_parts` come from server/risk-review-trip-table.js; each
 * reader falls back gracefully for a snapshot stored before those fields
 * existed.
 */
import { format } from 'date-fns';
import { netProfitDollars } from '../utils/tripNetProfit.js';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86400000;

/** 'YYYY-MM-DD' is a calendar day and must stay one in every timezone —
 *  `new Date('2026-10-30')` is UTC midnight, i.e. Oct 29 in Pacific. Naive
 *  airline-local departures ('2026-10-14T06:00:00') already parse as local. */
export function parseDisplayDate(v) {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(DATE_ONLY);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Oct 14", or "Mar 15, 2027" outside the current year. */
export function shortDate(v, now = new Date()) {
  const d = parseDisplayDate(v);
  if (!d) return null;
  return format(d, d.getFullYear() === now.getFullYear() ? 'MMM d' : 'MMM d, yyyy');
}

/** Whole calendar days from today to `v` (negative once it has passed). */
export function daysUntil(v, now = new Date()) {
  const d = parseDisplayDate(v);
  if (!d) return null;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((startOf(d) - startOf(now)) / DAY_MS);
}

/** The trip total every paid figure on the page is measured against. */
export function tripTotal(a) {
  return Number(a?.total_amount) || Number(a?.grand_total_with_hold) || 0;
}

/** Paid progress, 0–100, unrounded — the table shows one decimal ("81.0%"),
 *  so ordering by the server's whole-number percentage would misplace ties.
 *  On a trip with Faye travel insurance it is measured on travel, the premium
 *  and its share of the cash out of both sides, like the server's
 *  paid_percentage; the Total column still shows everything owed. */
export function paidPct(a) {
  const total = tripTotal(a);
  if (!(total > 0)) return 0;
  const paid = Number(a?.paid_to_date);
  const premium = Number(a?.insurance_premium) || 0;
  if (Number.isFinite(paid) && premium > 0) {
    const travelTotal = total - premium;
    if (!(travelTotal > 0)) return 0;
    const travelPaid = paid - (Number(a?.insurance_premium_paid) || 0);
    return Math.min(100, Math.max(0, (travelPaid / travelTotal) * 100));
  }
  if (Number.isFinite(paid)) return Math.min(100, Math.max(0, (paid / total) * 100));
  if (a?.paid_percentage != null) return Math.min(100, Math.max(0, Number(a.paid_percentage) || 0));
  return 0;
}

export function fmtPct(pct) {
  return pct >= 99.95 ? '100%' : `${pct.toFixed(1)}%`;
}

/** Booked ticket cost in dollars, or null when the row can't say. */
export function bookedBase(a) {
  const base = Number(a?.booked_base);
  if (base > 0) return base;
  const lf = Number(a?.live_fare?.booked_base_cents);
  return lf > 0 ? lf / 100 : null;
}

/** Today's price for the same trip (Refresh + AI's live check), or null.
 *  The delta is the check's own, measured against the base it priced. */
export function baseNow(a) {
  const lf = a?.live_fare;
  const current = Number(lf?.current_price_cents);
  const booked = Number(lf?.booked_base_cents);
  if (!(current > 0) || !(booked > 0)) return null;
  const deltaCents = Number.isFinite(Number(lf.delta_cents)) && lf.delta_cents != null
    ? Number(lf.delta_cents)
    : current - booked;
  return {
    current: current / 100,
    deltaCents,
    deltaPct: (deltaCents / booked) * 100,
    direction: deltaCents >= 100 ? 'up' : deltaCents <= -100 ? 'down' : 'flat',
    exact: !!lf.matched_exact_flight,
    fetchedAt: lf.fetched_at || null,
  };
}

/**
 * Net profit (founder, 2026-10-03): what Defera keeps. That is the trip
 * page's Net Margin less the backer's cut, the miles liability and card
 * processing, the bar the backer script holds trips to
 * (src/utils/tripNetProfit.js). Risk Review trips are unticketed, so the
 * ticket is priced at today's fare (Base now) until an actual cost is
 * entered. null when the row predates the field or nothing prices the
 * ticket yet.
 */
export function netProfit(a) {
  const parts = a?.net_profit_parts;
  if (!parts) return null;
  const actual = Number(parts.actual_cost) || 0;
  const ticketCost = actual > 0 ? actual : (baseNow(a)?.current || 0);
  if (!(ticketCost > 0)) return null;
  return {
    amount: Math.round(netProfitDollars(parts, ticketCost) * 100) / 100,
    ticketCost,
    basis: actual > 0 ? 'actual' : 'today',
  };
}

const usd = (n, digits = 2) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits,
}).format(n);

/** "+$43", "−$1,184", "$0": whole dollars, signed, the minus a true minus. */
export function fmtNetProfit(amount) {
  const whole = Math.round(Number(amount) || 0);
  if (whole === 0) return '$0';
  return `${whole < 0 ? '−' : '+'}${usd(Math.abs(whole), 0)}`;
}

/** The cell's hover breakdown, one line per term, adding up to the cent. */
export function netProfitLines(a) {
  const np = netProfit(a);
  if (!np) {
    return a?.net_profit_parts
      ? ["Needs today's fare. Refresh + AI checks it for each trip"]
      : ['Net profit arrives with the next Refresh'];
  }
  const p = a.net_profit_parts;
  const premium = Number(a?.insurance_premium) || 0;
  const lines = [`Trip ${usd(p.sold)}${premium > 0 ? ' (Faye premium out)' : ''}`];
  if (p.skip_fees > 0) lines.push(`+ Skip fees ${usd(p.skip_fees)}`);
  if (p.seat_margin) lines.push(`${p.seat_margin > 0 ? '+' : '−'} Seat purchase ${usd(Math.abs(p.seat_margin))}`);
  if (p.refunded > 0) lines.push(`− Refunded ${usd(p.refunded)}`);
  lines.push(np.basis === 'actual'
    ? `− Actual cost ${usd(np.ticketCost)}`
    : `− Ticket at today's fare ${usd(np.ticketCost)}`);
  if (p.backer_cut > 0) lines.push(`− Backer's cut ${usd(p.backer_cut)}`);
  if (p.miles > 0) lines.push(`− Miles ${usd(p.miles)}`);
  if (p.processing > 0) lines.push(`− Card fees ${usd(p.processing)} (${+(p.processing_rate * 100).toFixed(2)}%)`);
  lines.push(`= Net profit ${np.amount < 0 ? '−' : ''}${usd(Math.abs(np.amount))}`);
  return lines;
}

/**
 * A row as the per-trip AI sees it. Its previous verdict stays out (no
 * feeding a run back into itself), and so do the net profit inputs: the
 * column is display only, and an extra field in the prompt could move a
 * verdict that is meant to stay put for the same trip.
 */
export function aiTripFeatures(a) {
  const features = { ...(a || {}) };
  delete features.ai_assessment;
  delete features.net_profit_parts;
  return features;
}

/** 'past_due' | 'retrying' | 'delinquent' | null. Older snapshots carry only
 *  the Stripe signals, so those still flag. */
export function paymentIssue(a) {
  if (a?.payment_issue) return a.payment_issue;
  return a?.subscription_status === 'past_due' || a?.last_payment_failed ? 'past_due' : null;
}

export const PAYMENT_ISSUE_COPY = {
  past_due: { label: 'Past due', title: 'Stripe plan is past due — the latest charge failed' },
  retrying: { label: 'Past due', title: 'NMI declined a charge — a retry is scheduled' },
  delinquent: { label: 'Delinquent', title: 'NMI declined a charge for good — this trip is heading to default' },
};

/** Some accounts store the name twice ("Cassandra Tutson Cassandra Tutson"). */
export function customerName(a) {
  const raw = String(a?.user_full_name || '').trim().replace(/\s+/g, ' ');
  if (raw) {
    const parts = raw.split(' ');
    const half = parts.length / 2;
    if (parts.length % 2 === 0
      && parts.slice(0, half).join(' ').toLowerCase() === parts.slice(half).join(' ').toLowerCase()) {
      return parts.slice(0, half).join(' ');
    }
    return raw;
  }
  return a?.user_email && a.user_email !== '—' ? a.user_email : 'Unknown customer';
}

export function tripRef(a) {
  return a?.booking_reference || a?.trip_reference || null;
}

/** Last-payment cell: the date plus how to read it. */
export function lastPayment(a) {
  const date = a?.last_payment_date || null;
  const basis = a?.last_payment_basis || null;
  return { date, basis, label: shortDate(date) };
}

/**
 * The two rankings (founder, 2026-10-02): "Soonest departure and most
 * percentage paid is my priorities … I should be able to switch back and
 * forth, but should always be ranked by soonest departure regardless of
 * percentage paid or whatever else." So departure decides the order in BOTH:
 *
 *   departure  departure day, soonest first; trips leaving the same day put
 *              the more-paid one first
 *   pct_paid   10-point paid bands, highest first (90%+, 80s, 70s …); inside
 *              a band, the departure order above
 *
 * Days, not instants: the table shows dates, so two "Oct 22" rows must order
 * by paid, not by which one leaves earlier that morning.
 */
export const RANKINGS = ['departure', 'pct_paid'];

export const SORT_LABELS = {
  departure: 'soonest departure first',
  pct_paid: 'most paid first, soonest departure in each band',
};

/** Local midnight of the departure day, or null. */
export function departureDayMs(a) {
  const d = parseDisplayDate(a?.first_departure_date);
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() : null;
}

/** 10-point paid band: floor 0…90 (100% joins the 90s) and its label.
 *  Banded on the figure the row SHOWS (one decimal), so a 79.96% trip that
 *  reads "80.0%" never sits under a "70–79% paid" header. */
export function paidBand(a) {
  const pct = Number(paidPct(a).toFixed(1));
  const floor = pct >= 90 ? 90 : Math.floor(pct / 10) * 10;
  const label = floor === 90 ? '90%+ paid'
    : floor === 0 ? 'Under 10% paid'
      : `${floor}–${floor + 9}% paid`;
  return { floor, label };
}

const orInf = (v) => (v == null ? Number.POSITIVE_INFINITY : v);
const msOrInf = (v) => orInf(parseDisplayDate(v)?.getTime());

/** Departure day ↑, then paid ↓, then the exact departure time ↑. */
function byDeparture(x, y) {
  return (orInf(departureDayMs(x)) - orInf(departureDayMs(y)))
    || (paidPct(y) - paidPct(x))
    || (msOrInf(x.first_departure_date) - msOrInf(y.first_departure_date));
}

/** Returns a NEW array ranked by `departure` (default) or `pct_paid`. */
export function sortTrips(rows, sortBy) {
  const out = [...rows];
  if (sortBy === 'pct_paid') {
    return out.sort((x, y) => (paidBand(y).floor - paidBand(x).floor) || byDeparture(x, y));
  }
  return out.sort(byDeparture);
}

/**
 * Consecutive runs of an already-sorted list. Under `pct_paid` each paid
 * band becomes a labelled group so the reason a later departure sits above
 * an earlier one is on screen; under `departure` it is one unlabelled group.
 */
export function groupTrips(rows, sortBy) {
  if (sortBy !== 'pct_paid') return [{ key: 'all', label: null, rows }];
  const groups = [];
  for (const a of rows) {
    const band = paidBand(a);
    const last = groups[groups.length - 1];
    if (last && last.key === `band-${band.floor}`) last.rows.push(a);
    else groups.push({ key: `band-${band.floor}`, label: band.label, rows: [a] });
  }
  return groups;
}

/** Paid filter: 'all' or a minimum whole percentage ('70' → 70%+). */
export function passesPaidFilter(a, minPaid) {
  if (!minPaid || minPaid === 'all') return true;
  return paidPct(a) >= Number(minPaid);
}
